import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../../utils/date";
import { toast } from "react-toastify";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { usePlan } from "../../../../hooks/usePlan";
import { usePermissions } from "../../../../hooks/usePermissions";
import MemberService from "../../../../services/member_service";
import AuthService from "../../../../services/auth_service";
import { useMembers } from "../../../../hooks/useMembers";

import useProfileData from "./useProfileData";
import useProfileForm from "./useProfileForm";
import useProfileNavigation from "./useProfileNavigation";
import useProfileModals from "./useProfileModals";
import useEnrollments from "./useEnrollments";
import useMemberMemberships from "./useMemberMemberships";
import useMemberFinancials from "./useMemberFinancials";

import getMembershipColumns from "../components/Columns/MembershipColumns";
import getEnrollmentColumns from "../components/Columns/EnrollmentColumns";
import { getFinancialColumns, getInstallmentColumns } from "../components/Columns/FinancialColumns";
import { FEATURE_KEYS } from "../../../../constants/plans";
import { MEMBER_PROFILE_TABS } from "../constants";
import { ACTION_ACCESS_MAP } from "../../../../constants/access";

/**
 * useProfile
 * 
 * Orquestrador do módulo MemberProfile.
 * Compõe todos os hooks especializados e monta o retorno
 * estruturado para consumo direto pelo index.js.
 * 
 * NÃO contém lógica de negócio própria — apenas composição.
 */
const useProfile = () => {
    const { addOrUpdateMember, removeMember } = useMembers();
    const navigate = useNavigate();

    // ─── Hooks especializados ───
    const profileData = useProfileData();
    const { member, setMember, idTenant, user, fetchMember } = profileData;

    const profileForm = useProfileForm(member, setMember, idTenant, user);
    const navigation = useProfileNavigation(member);
    const modals = useProfileModals(idTenant);

    // ─── Sub-hooks de domínio ───
    const enrollmentsHook = useEnrollments(member);
    const membershipsHook = useMemberMemberships(member);
    const financialsHook = useMemberFinancials(member);
    const { canAccess } = useAuthorization();
    const { hasFeature } = usePlan();
    const { isOwner } = usePermissions();

    const canViewFinancialTab = canAccess(ACTION_ACCESS_MAP.MEMBER_FINANCIAL_TAB);
    const canViewMembershipsTab = canAccess(ACTION_ACCESS_MAP.MEMBER_MEMBERSHIPS_TAB);
    const canCreateSale = canAccess(ACTION_ACCESS_MAP.MEMBER_NEW_SALE);
    const canPayDebt = canAccess(ACTION_ACCESS_MAP.MEMBER_PAY_DEBT);
    const canActivateMember = !hasFeature(FEATURE_KEYS.FINANCIAL_MODULE)
        && !!member
        && (member.status === "inactive" || member.status === "lead");

    const canDeactivateMember = !hasFeature(FEATURE_KEYS.FINANCIAL_MODULE)
        && !!member
        && (member.status === "active");

    const canDeleteMember = isOwner && !!member && member.status !== "deleted";

    // ─── Delete modal ───
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletionBlockers, setDeletionBlockers] = useState([]);
    const [deletionBlockersLoading, setDeletionBlockersLoading] = useState(false);

    const handleOpenDeleteModal = useCallback(async () => {
        if (!member?.id || !idTenant) return;
        setIsDeleteModalOpen(true);
        setDeletionBlockers([]);
        setDeletionBlockersLoading(true);
        try {
            const blockers = await MemberService.checkDeletionBlockers(idTenant, member.id);
            setDeletionBlockers(blockers);
        } catch (e) {
            toast.error("Erro ao verificar vínculos do membro.");
            setIsDeleteModalOpen(false);
        } finally {
            setDeletionBlockersLoading(false);
        }
    }, [idTenant, member]);

    const handleConfirmDelete = useCallback(async (password) => {
        await AuthService.reauthenticate(password);
        await MemberService.softDelete(idTenant, member.id, user, member.fullName);
        // Navega primeiro para evitar re-render do perfil com membro deletado
        const memberName = member.fullName;
        const memberId = member.id;
        navigate("/members", { replace: true });
        removeMember(memberId);
        toast.success(`"${memberName}" foi excluído com sucesso.`);
    }, [idTenant, member, navigate, removeMember, user]);

    const visibleTabs = MEMBER_PROFILE_TABS.filter((tab) => {
        if (tab.id === "2") return canViewFinancialTab;
        if (tab.id === "4") return canViewMembershipsTab;
        return true;
    });

    // ─── Tabs ───
    const [activeTab, setActiveTab] = useState("1");

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    useEffect(() => {
        const activeTabStillVisible = visibleTabs.some((tab) => tab.id === activeTab);
        if (!activeTabStillVisible && visibleTabs.length > 0) {
            setActiveTab(visibleTabs[0].id);
        }
    }, [activeTab, visibleTabs]);

    /** Ao clicar em editar, sempre volta para a aba "Meus Dados" */
    const handleEdit = () => {
        profileForm.handleEdit();
        setActiveTab("1");
    };

    const handleActivateMember = useCallback(async () => {
        if (!member?.id || !idTenant) return;

        try {
            await MemberService.activateMember(idTenant, member.id, user, member.fullName);
            addOrUpdateMember({ id: member.id, status: "active" });
            toast.success("Aluno ativado com sucesso.");
            await fetchMember(true);
        } catch (error) {
            console.error("Erro ao ativar membro:", error);
            toast.error(error.message || "Erro ao ativar aluno.");
        }
    }, [addOrUpdateMember, fetchMember, idTenant, member, user]);

    const handleDeactivateMember = useCallback(async () => {
        if (!member?.id || !idTenant) return;

        try {
            await MemberService.deactivateMember(idTenant, member.id, user, member.fullName);
            addOrUpdateMember({ id: member.id, status: "inactive" });
            toast.success("Aluno inativado com sucesso.");
            await fetchMember(true);
        } catch (error) {
            console.error("Erro ao inativar membro:", error);
            toast.error(error.message || "Erro ao inativar aluno.");
        }
    }, [addOrUpdateMember, fetchMember, idTenant, member, user]);

    // ─── Refresh combinado de planos ───
    const { refresh: refreshMemberships } = membershipsHook;
    const handleMembershipRefresh = useCallback(async () => {
        await refreshMemberships();
        await new Promise(resolve => setTimeout(resolve, 2000));
        await fetchMember(false);
    }, [fetchMember, refreshMemberships]);

    // ─── Colunas de tabelas (montadas com callbacks dos modais) ───
    const enrollmentColumns = getEnrollmentColumns({
        formatDate,
        canceling: enrollmentsHook.canceling,
        onCancel: enrollmentsHook.handleCancelEnrollment,
    });

    const membershipColumns = getMembershipColumns({
        formatDate,
        onViewDetails: modals.handleViewMembershipDetails,
        onPrintReceipt: modals.handlePrintReceipt,
    });

    const financialColumns = getFinancialColumns({
        formatDate,
        onViewSale: (row) => modals.handlePrintReceipt(row),
    });

    const installmentColumns = getInstallmentColumns({ formatDate });

    // ─── Retorno estruturado ───
    return {
        // Estado geral
        loading: profileData.loading,
        error: profileData.error,
        member,

        // Edição
        formik: profileForm.formik,
        isEditing: profileForm.isEditing,
        handleEdit,
        handleSave: profileForm.handleSave,
        handleCancelEdit: profileForm.handleCancelEdit,
        handlePhotoChange: profileForm.handlePhotoChange,
        handleActivateMember,
        handleDeactivateMember,

        // Tabs
        activeTab,
        toggleTab,
        visibleTabs,

        // Navegação
        handleNewSale: navigation.handleNewSale,
        handlePayDebt: navigation.handlePayDebt,
        handleNewEnrollment: navigation.handleNewEnrollment,
        canCreateSale,
        canPayDebt,
        canActivateMember,
        canDeactivateMember,
        canDeleteMember,
        canViewFinancialTab,
        canViewMembershipsTab,

        // Delete
        isDeleteModalOpen,
        toggleDeleteModal: () => setIsDeleteModalOpen(v => !v),
        deletionBlockers,
        deletionBlockersLoading,
        handleOpenDeleteModal,
        handleConfirmDelete,

        // Matrículas
        enrollments: enrollmentsHook.enrollments,
        enrollmentsLoading: enrollmentsHook.loading,
        enrollmentColumns,

        // Planos
        memberMemberships: membershipsHook.memberships,
        membershipsLoading: membershipsHook.loading,
        membershipColumns,

        // Financeiro
        financials: financialsHook.financials,
        financialsLoading: financialsHook.loading,
        financialColumns,
        installmentColumns,
        totalDebt: financialsHook.totalDebt,

        // Modal: Ações do Plano
        isActionsModalOpen: modals.isActionsModalOpen,
        toggleActionsModal: modals.toggleActionsModal,
        selectedMembership: modals.selectedMembership,
        handleMembershipRefresh,

        // Modal: Recibo
        receiptModalOpen: modals.receiptModalOpen,
        toggleReceiptModal: modals.toggleReceiptModal,
        currentReceiptData: modals.currentReceiptData,
    };
};

export default useProfile;
